import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { getStaffMembers } from '../../../firebase/services/userService';
import { User } from '../../../models/types';
import { getRoles } from '../../../firebase/services/roleService';
import { Role } from '../../../types/rbac';

const StaffManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [roles, setRoles] = useState<Role[]>([]);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const staffMembers = await getStaffMembers();
        setStaff(staffMembers);
      } catch (err) {
        console.error('Error fetching staff members:', err);
        setError('Failed to load staff members');
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, []);

  useEffect(() => {
    getRoles().then(setRoles);
  }, []);

  const handleRoleFilterChange = (event: SelectChangeEvent) => {
    setRoleFilter(event.target.value);
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  const getRoleName = (roleId: string) => roles.find(r => r.id === roleId)?.name || '';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Staff Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/business/staff/new')}
        >
          Add Staff Member
        </Button>
      </Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="role-filter-label">Filter by Role</InputLabel>
                <Select
                  labelId="role-filter-label"
                  id="role-filter"
                  value={roleFilter}
                  label="Filter by Role"
                  onChange={handleRoleFilterChange}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="staff">Staff</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {staff.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staff
                .filter(staffMember => roleFilter === 'all' || getRoleName(staffMember.roleId) === roleFilter)
                .map(staffMember => (
                  <TableRow key={staffMember.id}>
                    <TableCell component="th" scope="row">
                      {staffMember.firstName} {staffMember.lastName}
                    </TableCell>
                    <TableCell>{staffMember.email}</TableCell>
                    <TableCell>{formatPhoneNumber(staffMember.phone)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getRoleName(staffMember.roleId)} 
                        color={getRoleName(staffMember.roleId) === 'admin' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => navigate(`/admin/business/staff/edit/${staffMember.id}`)}
                        color="primary"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Staff Members
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              You haven't added any staff members yet.
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/admin/business/staff/new')}
              startIcon={<AddIcon />}
            >
              Add First Staff Member
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default StaffManagementPage; 